import React, { ReactNode, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { theme } from '../theme/mui-theme';
import { AuthProvider } from '../../contexts/identityaccess/application/hooks/AuthContext';
import { HttpClient } from '../../contexts/common/infrastructure/http/HttpClient';
import { HttpAuthRepository } from '../../contexts/identityaccess/infrastructure/http/HttpAuthRepository';
import { AuthService } from '../../contexts/identityaccess/application/services/AuthService';
import { tokenStorage } from '../../contexts/identityaccess/infrastructure/storage/TokenStorage';
import { AuthInterceptor } from '../../contexts/common/infrastructure/http/interceptors/AuthInterceptor';
import { RefreshTokenInterceptor } from '../../contexts/common/infrastructure/http/interceptors/RefreshTokenInterceptor';
import { ErrorInterceptor } from '../../contexts/common/infrastructure/http/interceptors/ErrorInterceptor';
import { HttpAccountRepository } from '../../contexts/identityaccess/infrastructure/http/HttpAccountRepository';
import { AccountService } from '../../contexts/identityaccess/application/services/AccountService';
import { HttpApiClientRepository } from '../../contexts/identityaccess/infrastructure/http/HttpApiClientRepository';
import { ApiClientService } from '../../contexts/identityaccess/application/services/ApiClientService';
import { HttpDeadLetterRepository } from '../../contexts/messaging/infrastructure/http/HttpDeadLetterRepository';
import { DeadLetterService } from '../../contexts/messaging/application/services/DeadLetterService';
import { ServicesProvider } from '../contexts/ServicesContext';
import { NotificationProvider } from '../../shared/contexts/NotificationContext';
import { BreadcrumbProvider } from '../../shared/contexts/BreadcrumbContext';
import { API_CONFIG } from '../config/api.config';
import { AUTH_CONFIG } from '../config/auth.config';
import { CookieManager } from '../../contexts/identityaccess/infrastructure/storage/CookieManager';

/**
 * AppProviders component
 * Wraps the application with all necessary providers and dependency injection
 */
interface AppProvidersProps {
  children: ReactNode;
}

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  /**
   * Initialize infrastructure dependencies
   * Memoized to ensure single instances
   */
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      }),
    [],
  );

  const { authService, accountService, apiClientService, deadLetterService } = useMemo(() => {
    const adminHttpClient = new HttpClient(API_CONFIG.adminBaseURL, API_CONFIG.timeout);
    const axiosInstance = adminHttpClient.getAxiosInstance();

    // Register interceptors
    const authInterceptor = new AuthInterceptor(axiosInstance, () => tokenStorage.getAccessToken());
    authInterceptor.register();

    const refreshTokenInterceptor = new RefreshTokenInterceptor(
      axiosInstance,
      AUTH_CONFIG.refreshTokenEndpoint,
      (accessToken) => tokenStorage.setAccessToken(accessToken),
      () => {
        // On refresh failure, clear token and redirect to login
        tokenStorage.clearAccessToken();
        CookieManager.deleteCookie(AUTH_CONFIG.refreshTokenFlagCookie);
        window.location.href = AUTH_CONFIG.logoutRedirect;
      },
    );
    refreshTokenInterceptor.register();

    const errorInterceptor = new ErrorInterceptor(axiosInstance);
    errorInterceptor.register();

    // Create repositories
    const authRepository = new HttpAuthRepository(adminHttpClient, API_CONFIG.clientId);
    const accountRepository = new HttpAccountRepository(adminHttpClient);
    const apiClientRepository = new HttpApiClientRepository(adminHttpClient);
    const deadLetterRepository = new HttpDeadLetterRepository(adminHttpClient);

    // Create services
    const authService = new AuthService(
      authRepository,
      (token) => tokenStorage.setAccessToken(token.accessToken),
      () => tokenStorage.clearAccessToken(),
    );

    const accountService = new AccountService(accountRepository);
    const apiClientService = new ApiClientService(apiClientRepository);
    const deadLetterService = new DeadLetterService(deadLetterRepository);

    return { authService, accountService, apiClientService, deadLetterService };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <NotificationProvider>
            <AuthProvider authService={authService}>
              <ServicesProvider
                accountService={accountService}
                apiClientService={apiClientService}
                deadLetterService={deadLetterService}
              >
                <BreadcrumbProvider>{children}</BreadcrumbProvider>
              </ServicesProvider>
            </AuthProvider>
          </NotificationProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};
